import { BrowserRouter, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState, useTransition } from 'react';
import * as Sentry from '@sentry/react';
import Routes from './Routes';
import './index.scss';
import HubSpotButton from 'lib.components/src/components/HubSpotButton/HubSpotButton.tsx';
import LoadingView from 'lib.components/src/components/common/LoadingView';
import { isChunkLoadError, reloadForChunkError } from './utils/chunkReload';

import 'lib.styles/src/antd_styles.scss';
import './fonts.scss';
import 'lib.styles/src/desk.scss';
import { AnalyticsProvider } from 'lib.analytics';
import { AuthProvider } from 'lib.context/src/AuthContext';
import { LoaderMainProvider } from 'lib.context/src/LoaderMainContext';
import { TourProvider } from 'lib.context/src/TourContext';
import { ExitPageProvider } from 'lib.context/src/ExitPageContext';
import { BillingProvider } from 'lib.context/src/BillingContext';
import { PageProvider } from 'lib.context/src/PageContext';
import { ResponsiveAntdProvider } from './providers/ResponsiveAntdProvider';
import { FeaturesProvider } from 'lib.context/src/FeaturesContext';
import { AiAssistantProvider } from 'lib.context/src/AiAssistantContext';
import { AgentUnreadProvider } from 'lib.context/src/AgentUnreadContext';
import { AsyncTaskProvider } from 'lib.context/src/AsyncTaskContext';
import AnalyticsIdentityProvider from './providers/AnalyticsIdentityProvider';

const AiAssistant = lazy(() => import('lib.components/src/components/AiAssistant'));

function App() {
  return (
    <AnalyticsProvider>
      <AuthProvider>
        <AnalyticsIdentityProvider>
          <FeaturesProvider>
            <ExitPageProvider>
              <TourProvider>
                <BrowserRouter>
                  <PageProvider>
                    <LoaderMainProvider>
                      <ResponsiveAntdProvider>
                        <BillingProvider>
                          <AiAssistantProvider>
                            <AgentUnreadProvider>
                              <AsyncTaskProvider>
                                <Sentry.ErrorBoundary
                                  fallback={({ error }) => <RouteErrorFallback error={error} />}
                                >
                                  <RoutesWithSuspense />
                                </Sentry.ErrorBoundary>
                                <GlobalWidgets />
                              </AsyncTaskProvider>
                            </AgentUnreadProvider>
                          </AiAssistantProvider>
                        </BillingProvider>
                      </ResponsiveAntdProvider>
                    </LoaderMainProvider>
                  </PageProvider>
                </BrowserRouter>
              </TourProvider>
            </ExitPageProvider>
          </FeaturesProvider>
        </AnalyticsIdentityProvider>
      </AuthProvider>
    </AnalyticsProvider>
  );
}

export default App;

/** Routes where global floating widgets must never render (public embedded pages). */
// the legacy links redirect to the start page — no widget mounts on the way
const WIDGETLESS_PATH_PREFIXES = ['/start/partner/', '/presentation/partner/', '/pitch/partner/'];

/**
 * Global floating widgets (HubSpot chat, AI assistant, Loom player) —
 * suppressed on public pages such as the partner start page, where
 * third-party chrome would break the experience.
 *
 * @returns the widgets or null on widgetless routes
 */
const GlobalWidgets = () => {
  const { pathname } = useLocation();

  if (WIDGETLESS_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return null;
  }

  return (
    <>
      <HubSpotButton />
      <AiAssistant />
    </>
  );
};

/**
 * Fallback rendered when a route subtree throws. Stale-chunk errors trigger a
 * single guarded reload (to pull the fresh build); any other error shows a
 * minimal recovery screen with a manual reload (Sentry already captured it via
 * the surrounding boundary), instead of leaving the user on a blank white page.
 *
 * @param error - the error captured by the error boundary
 * @returns the fallback element
 */
const RouteErrorFallback = ({ error }: { error: unknown }) => {
  const isChunkError = isChunkLoadError(error);

  useEffect(() => {
    if (isChunkError) {
      reloadForChunkError();
    }
  }, [isChunkError]);

  // keep the screen neutral while the automatic reload kicks in
  if (isChunkError) {
    return <LoadingView />;
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        padding: 24,
        textAlign: 'center',
      }}
    >
      <h2 style={{ margin: 0 }}>Something went wrong</h2>
      <p style={{ margin: 0, color: '#6b6f76' }}>
        Please reload the page. If the problem persists, contact support.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          padding: '8px 20px',
          border: 'none',
          borderRadius: 8,
          background: '#2f6bff',
          color: '#fff',
          cursor: 'pointer',
          fontSize: 14,
        }}
      >
        Reload
      </button>
    </div>
  );
};

/** A View Transition handle exposing the promise that settles when it is ready. */
type ViewTransitionLike = { ready?: Promise<unknown> };

const RoutesWithSuspense = () => {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    if (location !== displayLocation) {
      // the same page with another query (a tab, a step, a filter) swaps in
      // place: a whole-page cross-fade there shows both states at once
      if (location.pathname === displayLocation.pathname) {
        setDisplayLocation(location);
        return;
      }
      // Use View Transitions API if available for smooth animations
      if (typeof document !== 'undefined' && 'startViewTransition' in document) {
        const doc = document as Document & {
          startViewTransition?: (callback: () => void | Promise<void>) => ViewTransitionLike;
        };

        const transition = doc.startViewTransition?.(() => {
          startTransition(() => {
            setDisplayLocation(location);
          });
        });

        // the ready promise rejects with InvalidStateError when a transition is
        // interrupted by a newer navigation — expected, swallow to avoid noisy
        // unhandled rejections (Sentry JAVASCRIPT-REACT-1CA)
        transition?.ready?.catch(() => {});
      } else {
        // Fallback for browsers without View Transitions API
        startTransition(() => {
          setDisplayLocation(location);
        });
      }
    }
  }, [location, displayLocation]);

  const fallback = isPending ? <Routes locationOverride={displayLocation} /> : <LoadingView />;

  return (
    <Suspense fallback={fallback}>
      <Routes locationOverride={displayLocation} />
    </Suspense>
  );
};
